import { computeGstAmountForUnit } from '../gst';
import { isOpenChangeOrderStatus } from '../changeOrders';
import { activeBookingCustomerReceipts, type BankTransactionRow, type BookingMeta, toMs } from './collections';
import { buildDueSchedule, daysBetween, toYmd, unitDues } from './dues';
import { pendingStepsForUnit, sanctionOf, type PendingStep, type WorkflowCase, type WorkflowDefinitionStep } from './workflowBoard';
import { buildingBucReceived, fullPaymentReceived } from '../deedPossessionGates';

export type TodayUnit = {
  id: string;
  unitNumber?: string | null;
  unitAmount?: number | null;
  bookingStatus?: string | null;
  activeBookingId?: string | null;
  saleAgreementDate?: string | null;
  primaryCustomerName?: string | null;
  customerName?: string | null;
  allotteeName?: string | null;
  [key: string]: any;
};

export type TodayBookingInfo = BookingMeta & {
  bookingId: string;
  customerName?: string;
  bookingDateMs?: number;
  agreementDone: boolean;
  saleDeedDone: boolean;
  booking: Record<string, any> | null;
  mainCase?: WorkflowCase | null;
  loanCase?: WorkflowCase | null;
};

export type TodayChangeOrder = {
  id: string;
  coNo?: string;
  unitNo?: string;
  title?: string;
  priority?: string;
  status?: string;
  customerApproval?: string;
  dueDate?: any;
};

export type TodayMaterialOrder = {
  id: string;
  materialLabel?: string;
  expectedDate?: string;
  orderQty?: number;
  unit?: string;
  vendor?: string;
  receipts?: Array<{ qty?: number }>;
  sourceType?: string;
};

export type UnitStage = 'available' | 'blocked' | 'form' | 'booked' | 'agreement' | 'registered';

export type TodayUnitRow = {
  unitId: string;
  unitNo: string;
  buyer: string;
  value: number;
  received: number;
  pending: number;
  paidPct: number;
  stage: UnitStage;
  overdue: number;
  overdueDays: number;
  hasPlan: boolean;
  nextStep: string;
};

export type MonthPoint = { key: string; label: string; amount: number; demanded: number; isCurrent: boolean; isFuture: boolean };

export type TodayAction = {
  id: string;
  kind: 'call' | 'workflow' | 'loan' | 'changeOrders' | 'materials';
  title: string;
  sub: string;
  chip: [string, string];
  unitId?: string;
  path?: string;
  cta: string;
};

export type PaymentRow = { unitId: string; unitNo: string; buyer: string; label: string; amount: number; ms: number; days?: number };

export const STAGE_LABEL: Record<UnitStage, string> = {
  available: 'Available',
  blocked: 'On hold',
  form: 'Block',
  booked: 'Booked',
  agreement: 'Agreement done',
  registered: 'Sale deed done',
};

const MONTH_LABELS = ['Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar'];
const PRIORITY_RANK: Record<string, number> = { urgent: 0, high: 1, medium: 2, low: 3 };
const DAY = 86400000;
const LOAN_STUCK_DAYS = 15;
// RERA Section 13: no more than 10% of the price before the agreement for sale is registered.
const AGREEMENT_MAX_SHARE = 0.1;

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
const monthKey = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
const lakhs = (v: number) => (v >= 1e7 ? `₹${(v / 1e7).toFixed(2).replace(/\.?0+$/, '')} Cr` : v >= 1e5 ? `₹${(v / 1e5).toFixed(1).replace(/\.0$/, '')} L` : `₹${Math.round(v).toLocaleString('en-IN')}`);

export const buyerName = (unit: TodayUnit, info?: TodayBookingInfo): string =>
  String(unit.primaryCustomerName || unit.customerName || unit.allotteeName || info?.customerName || '').trim();

/** GSTR-3B for a month is due on the 20th of the next month. */
export function nextGstr3bDate(now: Date): Date {
  const thisMonth = new Date(now.getFullYear(), now.getMonth(), 20);
  return startOfDay(now) <= thisMonth.getTime() ? thisMonth : new Date(now.getFullYear(), now.getMonth() + 1, 20);
}

/** RERA quarterly progress update is due within 15 days of each quarter end (15 Jan, Apr, Jul, Oct). */
export function nextReraQuarterlyDate(now: Date): Date {
  const today = startOfDay(now);
  const year = now.getFullYear();
  const candidates = [0, 3, 6, 9].map((m) => new Date(year, m, 15)).concat(new Date(year + 1, 0, 15));
  return candidates.find((d) => d.getTime() >= today) as Date;
}

export function buildTodaySummary(args: {
  units: TodayUnit[];
  bookingInfoByUnitId: Map<string, TodayBookingInfo>;
  txns: BankTransactionRow[];
  projectMeta: Record<string, any> | null;
  changeOrders: TodayChangeOrder[];
  orders?: TodayMaterialOrder[];
  mainSteps?: WorkflowDefinitionStep[];
  loanSteps?: WorkflowDefinitionStep[];
  includeForms?: boolean;
  now?: Date;
}) {
  const { units, bookingInfoByUnitId, txns, projectMeta, changeOrders } = args;
  const orders = args.orders || [];
  const mainSteps = args.mainSteps || [];
  const loanSteps = args.loanSteps || [];
  const now = args.now || new Date();
  const todayStart = startOfDay(now);
  const thisMonthKey = monthKey(now);
  const prevMonthKey = monthKey(new Date(now.getFullYear(), now.getMonth() - 1, 1));

  const receipts = activeBookingCustomerReceipts(txns, units, bookingInfoByUnitId);
  const receivedByUnit = new Map<string, number>();
  const loanDisbursedByUnit = new Map<string, number>();
  let receivedThisMonth = 0;
  let receivedPrevMonth = 0;
  const receivedTodayRows: Array<{ unitId: string; amount: number; ms: number }> = [];
  const fyStartYear = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  const monthly: MonthPoint[] = MONTH_LABELS.map((label, i) => {
    const d = new Date(fyStartYear, 3 + i, 1);
    const key = monthKey(d);
    return { key, label, amount: 0, demanded: 0, isCurrent: key === thisMonthKey, isFuture: d.getTime() > now.getTime() && key !== thisMonthKey };
  });
  const monthIndex = new Map(monthly.map((m, i) => [m.key, i]));
  receipts.forEach((r) => {
    if (r.ms > now.getTime()) return;
    receivedByUnit.set(r.unitId, Number(receivedByUnit.get(r.unitId) || 0) + r.amount);
    if ((r.txn as any).isLoanDisbursement === true && r.amount > 0) loanDisbursedByUnit.set(r.unitId, Number(loanDisbursedByUnit.get(r.unitId) || 0) + r.amount);
    const key = monthKey(new Date(r.ms));
    if (key === thisMonthKey) receivedThisMonth += r.amount;
    if (key === prevMonthKey) receivedPrevMonth += r.amount;
    const idx = monthIndex.get(key);
    if (idx != null) monthly[idx].amount += r.amount;
    if (r.ms >= todayStart && r.amount > 0) receivedTodayRows.push({ unitId: r.unitId, amount: r.amount, ms: r.ms });
  });

  const gstPaidByUnit = new Map<string, number>();
  activeBookingCustomerReceipts(txns, units, bookingInfoByUnitId, 'gst').forEach((r) => {
    gstPaidByUnit.set(r.unitId, Number(gstPaidByUnit.get(r.unitId) || 0) + r.amount);
  });

  const inventory = { total: units.length, available: 0, blocked: 0, form: 0, booked: 0, agreement: 0, registered: 0 };
  let bookedValue = 0;
  let received = 0;
  let pending = 0;
  let gstDue = 0;
  let gstCollected = 0;
  let gstBalance = 0;
  let gstUnitsPending = 0;
  let unsoldValue = 0;
  let overdueTotal = 0;
  let due7 = 0;
  let due30 = 0;
  let noPlanUnits = 0;
  let agreementLate = 0;
  const noPlanUnitIds: string[] = [];
  const agreementLateUnitIds: string[] = [];
  const loansNotStartedUnitIds: string[] = [];
  const due7Units = new Set<string>();
  const overdueRows: PaymentRow[] = [];
  const weekRows: PaymentRow[] = [];
  const upcomingDues: PaymentRow[] = [];
  const pendingSteps: PendingStep[] = [];
  const loans = { needed: 0, sanctioned: 0, inProcess: 0, notStarted: 0, expected: 0, disbursed: 0, banks: new Map<string, number>(), stuck: [] as Array<{ unitId: string; days: number; bank: string }> };

  const unitRows: TodayUnitRow[] = units.map((unit) => {
    const unitId = String(unit.id || '').trim();
    const info = bookingInfoByUnitId.get(unitId);
    const value = Math.max(0, Number(unit.unitAmount || 0));
    const hasForm = !!String(unit.activeBookingId || '').trim();
    const status = String(unit.bookingStatus || '').toLowerCase();
    // Booked means the unit's status is booked or sold, as on the Units page. A booking form on an
    // available unit is only a form (its receipts still count as received), unless includeForms is on.
    const hasBooking = hasForm && (!!args.includeForms || status === 'booked' || status === 'sold');
    let stage: UnitStage;
    if (!hasForm) stage = status === 'blocked' ? 'blocked' : 'available';
    else if (!hasBooking) stage = 'form';
    else if (info?.saleDeedDone) stage = 'registered';
    else if (info?.agreementDone || String(unit.saleAgreementDate || '').trim()) stage = 'agreement';
    else stage = 'booked';
    inventory[stage] += 1;

    const unitReceived = hasForm ? Number(receivedByUnit.get(unitId) || 0) : 0;
    const unitPending = hasBooking ? Math.max(0, value - unitReceived) : 0;
    const buyer = hasForm ? buyerName(unit, info) : '';
    if (hasForm && !hasBooking) received += unitReceived;
    const unitNo = String(unit.unitNumber || unitId).trim();
    let overdue = 0;
    let overdueDays = 0;
    let hasPlan = false;
    let nextStep = '';

    if (hasBooking) {
      bookedValue += value;
      received += unitReceived;
      pending += unitPending;
      const explicitGst = Number(unit.gst ?? unit.gstAmount ?? unit.gst_amount ?? NaN);
      const due = Number.isFinite(explicitGst) && explicitGst > 0 ? explicitGst : computeGstAmountForUnit(value, unit as any, (projectMeta as any) || null, 1);
      const paid = Number(gstPaidByUnit.get(unitId) || 0);
      gstDue += due;
      gstCollected += paid;
      if (due > paid) {
        gstBalance += due - paid;
        gstUnitsPending += 1;
      }

      const bookingDateMs = Number(info?.bookingDateMs || 0);
      const schedule = bookingDateMs ? buildDueSchedule(info?.booking?.paymentCondition, toYmd(new Date(bookingDateMs)), value) : null;
      if (!schedule) {
        noPlanUnits += 1;
        noPlanUnitIds.push(unitId);
      }
      else {
        hasPlan = true;
        schedule.forEach((item) => {
          const idx = monthIndex.get(monthKey(new Date(item.dueMs)));
          if (idx != null) monthly[idx].demanded += item.amount;
        });
        const d = unitDues(schedule, unitReceived, now);
        overdue = d.overdue;
        if (overdue > 0) {
          overdueDays = daysBetween(d.oldestOverdueMs, todayStart);
          overdueTotal += overdue;
          overdueRows.push({ unitId, unitNo, buyer, label: d.overdueLabel, amount: overdue, ms: d.oldestOverdueMs, days: overdueDays });
        }
        d.upcoming.forEach((item) => {
          if (item.dueMs <= todayStart + 7 * DAY) {
            due7 += item.unpaid;
            due7Units.add(unitId);
            weekRows.push({ unitId, unitNo, buyer, label: item.label, amount: item.unpaid, ms: item.dueMs });
          }
          if (item.dueMs <= todayStart + 30 * DAY) {
            due30 += item.unpaid;
            upcomingDues.push({ unitId, unitNo, buyer, label: item.label, amount: item.unpaid, ms: item.dueMs });
          }
        });
      }
      if (stage === 'booked' && value > 0 && unitReceived > value * AGREEMENT_MAX_SHARE) {
        agreementLate += 1;
        agreementLateUnitIds.push(unitId);
      }

      const steps = mainSteps.length ? pendingStepsForUnit({ unitId, mainCase: info?.mainCase, loanCase: info?.loanCase, mainSteps, loanSteps, fullPayment: fullPaymentReceived(value, unitReceived), bucReceived: buildingBucReceived(projectMeta, unit), registeredDeed: !!info?.saleDeedDone }) : [];
      pendingSteps.push(...steps);
      nextStep = steps.find((s) => s.status === 'ready')?.stepTitle || steps[0]?.stepTitle || '';

      if (String(info?.mainCase?.loanRequired || '').toLowerCase() === 'yes') {
        loans.needed += 1;
        loans.expected += Math.max(0, Number(unit.loanToDeveloperAmount || info?.booking?.paymentCondition?.loan?.requestedAmount || 0));
        loans.disbursed += Number(loanDisbursedByUnit.get(unitId) || 0);
        const loanCase = info?.loanCase;
        const sanction = sanctionOf(loanCase, loanSteps);
        const bank = sanction.bank || String(loanCase?.preferredBankName || '').trim();
        if (bank) loans.banks.set(bank, (loans.banks.get(bank) || 0) + 1);
        if (!loanCase) {
          loans.notStarted += 1;
          loansNotStartedUnitIds.push(unitId);
        }
        else if (sanction.sanctioned) loans.sanctioned += 1;
        else {
          loans.inProcess += 1;
          const startedMs = toMs(loanCase.startedAt);
          const days = startedMs ? daysBetween(startedMs, todayStart) : 0;
          if (days > LOAN_STUCK_DAYS) loans.stuck.push({ unitId, days, bank });
        }
      }
    } else {
      unsoldValue += value;
    }
    return {
      unitId,
      unitNo,
      buyer,
      value,
      received: unitReceived,
      pending: unitPending,
      paidPct: hasForm && value > 0 ? Math.min(100, Math.round((unitReceived / value) * 1000) / 10) : 0,
      stage,
      overdue,
      overdueDays,
      hasPlan,
      nextStep,
    };
  });

  const rowById = new Map(unitRows.map((r) => [r.unitId, r]));
  const openChangeOrders = changeOrders.filter((co) => isOpenChangeOrderStatus(co.status));
  const topChangeOrders = openChangeOrders
    .slice()
    .sort((a, b) => (PRIORITY_RANK[String(a.priority)] ?? 9) - (PRIORITY_RANK[String(b.priority)] ?? 9))
    .slice(0, 4);
  const urgentChangeOrders = openChangeOrders.filter((co) => co.priority === 'urgent').length;

  // Deliveries expected by today that have not fully arrived.
  const todayYmd = toYmd(now);
  const lateDeliveries = orders.filter((o) => {
    if (o.sourceType === 'direct' || !o.expectedDate || o.expectedDate > todayYmd) return false;
    const got = (o.receipts || []).reduce((s, r) => s + Number(r.qty || 0), 0);
    return got < Number(o.orderQty || 0);
  });

  overdueRows.sort((a, b) => b.amount - a.amount);
  weekRows.sort((a, b) => a.ms - b.ms);
  loans.stuck.sort((a, b) => b.days - a.days);

  // Workflow board: each unit sits at its first open main step.
  const journeySteps = mainSteps.filter((s) => s.enabled !== false && s.showInDashboard !== false);
  const unitsAtStep = new Map<string, string[]>();
  const firstOpenByUnit = new Map<string, PendingStep>();
  pendingSteps.forEach((p) => {
    if (p.workflowType !== 'main') return;
    const prev = firstOpenByUnit.get(p.unitId);
    if (!prev || (prev.status !== 'ready' && p.status === 'ready') || (prev.status === p.status && p.order < prev.order)) firstOpenByUnit.set(p.unitId, p);
  });
  const bookedIds = unitRows.filter((r) => r.stage === 'booked' || r.stage === 'agreement' || r.stage === 'registered').map((r) => r.unitId);
  bookedIds.forEach((id) => {
    const p = firstOpenByUnit.get(id);
    const key = p ? p.stepKey : 'all_done';
    unitsAtStep.set(key, [...(unitsAtStep.get(key) || []), id]);
  });
  const readySteps = pendingSteps.filter((p) => p.status === 'ready');
  const readyByTask = new Map<string, PendingStep[]>();
  readySteps.forEach((p) => {
    const key = `${p.workflowType}:${p.stepKey}`;
    readyByTask.set(key, [...(readyByTask.get(key) || []), p]);
  });
  const tasks = Array.from(readyByTask.values()).sort((a, b) => b.length - a.length);

  const actions: TodayAction[] = [];
  overdueRows.slice(0, 3).forEach((r) =>
    actions.push({ id: `call:${r.unitId}`, kind: 'call', title: `Call ${r.buyer || 'buyer'}, ${r.unitNo}`, sub: `${lakhs(r.amount)} overdue on ${r.label}`, chip: ['bad', `${r.days} days`], unitId: r.unitId, cta: 'Open unit' })
  );
  loans.stuck.slice(0, 2).forEach((l) => {
    const row = rowById.get(l.unitId);
    actions.push({ id: `loan:${l.unitId}`, kind: 'loan', title: `Loan not sanctioned, ${row?.unitNo || ''}`, sub: [row?.buyer, l.bank].filter(Boolean).join(', ') || 'Follow up with the bank', chip: ['warn', `${l.days} days`], unitId: l.unitId, cta: 'Follow up' });
  });
  tasks.slice(0, 2).forEach((group) => {
    const first = group[0];
    const unitsText = group.slice(0, 3).map((p) => rowById.get(p.unitId)?.unitNo).filter(Boolean).join(', ');
    actions.push({ id: `wf:${first.workflowType}:${first.stepKey}`, kind: 'workflow', title: `${first.stepTitle}: ${group.length} unit${group.length === 1 ? '' : 's'}`, sub: group.length > 3 ? `${unitsText} and ${group.length - 3} more` : unitsText, chip: ['acc', 'Ready'], unitId: first.unitId, cta: 'Start' });
  });
  if (urgentChangeOrders) actions.push({ id: 'co:urgent', kind: 'changeOrders', title: `${urgentChangeOrders} urgent change order${urgentChangeOrders === 1 ? '' : 's'}`, sub: topChangeOrders.filter((c) => c.priority === 'urgent').map((c) => c.title).filter(Boolean).slice(0, 2).join(', '), chip: ['bad', 'Urgent'], path: '/office/change-orders', cta: 'Open' });
  if (lateDeliveries.length) actions.push({ id: 'mat:late', kind: 'materials', title: `${lateDeliveries.length} material deliver${lateDeliveries.length === 1 ? 'y' : 'ies'} due`, sub: lateDeliveries.slice(0, 3).map((o) => o.materialLabel).filter(Boolean).join(', '), chip: ['warn', 'Due'], path: '/office/material-orders', cta: 'Open' });

  const statutory: PaymentRow[] = [
    { unitId: '', unitNo: '', buyer: '', label: 'GSTR-3B filing', amount: 0, ms: nextGstr3bDate(now).getTime() },
    { unitId: '', unitNo: '', buyer: '', label: 'RERA quarterly update', amount: 0, ms: nextReraQuarterlyDate(now).getTime() },
  ];
  const upcoming = [...upcomingDues.sort((a, b) => a.ms - b.ms).slice(0, 5), ...statutory].sort((a, b) => a.ms - b.ms);

  return {
    bookedUnits: inventory.booked + inventory.agreement + inventory.registered,
    bookedValue,
    received,
    pending,
    collectionPct: bookedValue > 0 ? Math.round((received / bookedValue) * 1000) / 10 : 0,
    receivedThisMonth,
    receivedPrevMonth,
    receivedToday: {
      amount: receivedTodayRows.reduce((s, r) => s + r.amount, 0),
      rows: receivedTodayRows.sort((a, b) => b.ms - a.ms).map((r) => ({ ...r, unitNo: rowById.get(r.unitId)?.unitNo || r.unitId, buyer: rowById.get(r.unitId)?.buyer || '' })),
    },
    unsoldValue,
    inventory,
    gst: { due: gstDue, collected: gstCollected, balance: gstBalance, unitsPending: gstUnitsPending },
    dues: {
      overdue: overdueTotal,
      overdueUnits: overdueRows.length,
      oldestDays: overdueRows.reduce((m, r) => Math.max(m, r.days || 0), 0),
      due7,
      due7Units: due7Units.size,
      due30,
      noPlanUnits,
      overdueRows,
      weekRows,
    },
    upcoming,
    actions,
    attention: {
      noPlanUnits, noPlanUnitIds,
      agreementLate, agreementLateUnitIds,
      notStarted: unitsAtStep.get('workflow_not_started')?.length || 0,
      notStartedUnitIds: unitsAtStep.get('workflow_not_started') || [],
      loansNotStarted: loans.notStarted, loansNotStartedUnitIds,
    },
    loans: { ...loans, banks: Array.from(loans.banks.entries()).sort((a, b) => b[1] - a[1]) },
    workflow: {
      hasDefinition: mainSteps.length > 0,
      steps: journeySteps.map((s) => ({ key: s.key, title: s.title, unitIds: unitsAtStep.get(s.key) || [] })),
      notStarted: unitsAtStep.get('workflow_not_started') || [],
      allDone: unitsAtStep.get('all_done') || [],
      readyCount: readySteps.length,
      blockedCount: pendingSteps.filter((p) => p.status === 'blocked').length,
      firstOpenByUnit,
      tasks: tasks.map((group) => ({ key: `${group[0].workflowType}:${group[0].stepKey}`, title: group[0].stepTitle, steps: group })),
    },
    monthly,
    unitRows,
    changeOrders: {
      open: openChangeOrders.length,
      urgent: urgentChangeOrders,
      waitingBuyer: openChangeOrders.filter((co) => co.customerApproval === 'pending').length,
      top: topChangeOrders,
    },
    dates: { gstr3b: nextGstr3bDate(now), rera: nextReraQuarterlyDate(now) },
  };
}

export type TodaySummary = ReturnType<typeof buildTodaySummary>;
